<template>
  <div class="flex flex-col gap-6">
    <!-- Vue d'ensemble -->
    <div class="flex flex-col gap-2">
      <h3 class="text-base font-semibold capitalize-first">
        {{ $t('global.invoicing_overview') }}
      </h3>
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.ca_ttc') }}
          </p>
          <p class="text-2xl font-bold">
            {{ formatCurrency(statistics.kpis.ca_ttc) }}
          </p>
          <p
            v-if="statistics.kpis.ca_ttc_evolution !== null"
            :class="evolutionClass(statistics.kpis.ca_ttc_evolution)"
            class="text-xs font-semibold"
          >
            {{ evolutionLabel(statistics.kpis.ca_ttc_evolution) }}
          </p>
        </div>

        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.ca_htva') }}
          </p>
          <p class="text-2xl font-bold">
            {{ formatCurrency(statistics.kpis.ca_htva) }}
          </p>
          <p
            v-if="statistics.kpis.ca_htva_evolution !== null"
            :class="evolutionClass(statistics.kpis.ca_htva_evolution)"
            class="text-xs font-semibold"
          >
            {{ evolutionLabel(statistics.kpis.ca_htva_evolution) }}
          </p>
        </div>

        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.hors_debours') }}
          </p>
          <p class="text-2xl font-bold">
            {{ formatCurrency(statistics.kpis.hors_debours) }}
          </p>
          <p
            v-if="statistics.kpis.hors_debours_evolution !== null"
            :class="evolutionClass(statistics.kpis.hors_debours_evolution)"
            class="text-xs font-semibold"
          >
            {{ evolutionLabel(statistics.kpis.hors_debours_evolution) }}
          </p>
        </div>

        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.solde_restant_du') }}
          </p>
          <p class="text-2xl font-bold">
            {{ formatCurrency(statistics.kpis.solde_restant_du) }}
          </p>
          <p
            v-if="statistics.kpis.solde_restant_du_evolution !== null"
            :class="evolutionClass(statistics.kpis.solde_restant_du_evolution)"
            class="text-xs font-semibold"
          >
            {{ evolutionLabel(statistics.kpis.solde_restant_du_evolution) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Statut des factures -->
    <div v-if="!kpiOnly" class="flex flex-col gap-2">
      <h3 class="text-base font-semibold capitalize-first">
        {{ $t('attributes.status') }}
      </h3>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.invoices_established') }}
          </p>
          <p class="text-2xl font-bold">
            {{ formatNumber(statistics.invoice_status.established_count) }}
          </p>
        </div>

        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.invoices_draft') }}
          </p>
          <p class="text-2xl font-bold">
            {{ formatNumber(statistics.invoice_status.draft_count) }}
          </p>
        </div>

        <div class="flex flex-col gap-1 rounded border p-4">
          <p class="text-xs font-medium text-gray-500 capitalize-first">
            {{ $t('global.recovery_rate') }}
          </p>
          <p class="text-2xl font-bold">
            {{ statistics.invoice_status.recovery_rate }}%
          </p>
          <p
            v-if="statistics.invoice_status.recovery_rate_evolution !== null"
            :class="evolutionClass(statistics.invoice_status.recovery_rate_evolution)"
            class="text-xs font-semibold"
          >
            {{ evolutionPtsLabel(statistics.invoice_status.recovery_rate_evolution) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Factures par tag -->
    <div
      v-if="!kpiOnly && statistics.tags_breakdown.length > 0"
      class="flex flex-col gap-2"
    >
      <h3 class="text-base font-semibold capitalize-first">
        {{ $t('global.invoices_by_tag') }}
      </h3>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <div
          v-for="tag in statistics.tags_breakdown"
          :key="tag.id"
          class="flex items-center gap-3 rounded border p-3"
        >
          <span
            class="bg-primary h-3 w-3 shrink-0 rounded-full"
            :data-tint="tag.tint ?? 'dark-gray'"
          />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium">{{ tag.name }}</p>
            <p class="text-xs text-gray-500">
              {{ formatNumber(tag.count) }} ({{ tag.percent }}%)
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Analyse par catégorie -->
    <div v-if="!kpiOnly" class="flex flex-col gap-2">
      <h3 class="text-base font-semibold capitalize-first">
        {{ $t('global.invoices_by_category') }}
      </h3>
      <div class="overflow-hidden rounded border">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
            <tr>
              <th class="px-4 py-2 text-left">{{ $t('attributes.category') }}</th>
              <th class="px-4 py-2 text-right">{{ $t('attributes.number') }}</th>
              <th class="px-4 py-2 text-right">{{ $t('global.ca_ttc') }}</th>
              <th class="px-4 py-2 text-right">{{ $t('global.ca_htva') }}</th>
              <th class="px-4 py-2 text-right">{{ $t('global.hors_debours') }}</th>
              <th class="px-4 py-2 text-right">{{ $t('global.ca_percent') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr
              v-for="row in statistics.categories_breakdown"
              :key="row.name ?? '__no_category__'"
              class="hover:bg-gray-50"
            >
              <td class="px-4 py-2 font-medium">
                <span v-if="row.name === null" class="text-gray-400 italic">
                  {{ $t('global.no_category') }}
                </span>
                <span v-else>{{ row.name }}</span>
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatNumber(row.nb) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatCurrency(row.ttc) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatCurrency(row.htva) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatCurrency(row.hors_debours) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-gray-500">
                {{ row.ca_percent }}%
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t-2 bg-gray-50 font-semibold">
            <tr>
              <td class="px-4 py-2 capitalize-first">{{ $t('global.total') }}</td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatNumber(categoryTotals.nb) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatCurrency(categoryTotals.ttc) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatCurrency(categoryTotals.htva) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatCurrency(categoryTotals.hors_debours) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-gray-500">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

export default defineComponent({
  name: 'AnalyticsInvoicingStats',
  props: {
    statistics: {
      type: Object,
      required: true
    },
    kpiOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      currency: 'metadata/getDefaultCurrencyMetadata'
    }),
    categoryTotals() {
      const rows = this.statistics.categories_breakdown ?? []
      return {
        nb: rows.reduce((sum, row) => sum + row.nb, 0),
        ttc: rows.reduce((sum, row) => sum + row.ttc, 0),
        htva: rows.reduce((sum, row) => sum + row.htva, 0),
        hors_debours: rows.reduce((sum, row) => sum + row.hors_debours, 0)
      }
    }
  },
  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat(this.$i18n.locale.replace('_', '-')).format(value)
    },
    formatCurrency(value) {
      return new Intl.NumberFormat(this.$i18n.locale.replace('_', '-'), {
        style: 'currency',
        currency: this.currency ?? 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },
    evolutionClass(value) {
      if (value > 0) return 'text-green-600'
      if (value < 0) return 'text-red-500'
      return 'text-gray-500'
    },
    evolutionLabel(value) {
      if (value > 0) return `+${value}%`
      if (value < 0) return `${value}%`
      return '0%'
    },
    evolutionPtsLabel(value) {
      if (value > 0) return `+${value} pts`
      if (value < 0) return `${value} pts`
      return '0 pts'
    }
  }
})
</script>
